import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { TONE_ICONS, type Tone } from './tones';
import { cn } from '../../lib/cn';

const toneClass: Record<Tone, string> = {
  neutral: 'bg-surface-muted text-text border-border-strong',
  primary: 'bg-primary-soft text-primary border-primary',
  info: 'bg-info-soft text-info border-info',
  success: 'bg-success-soft text-success border-success',
  warning: 'bg-warning-soft text-warning border-warning',
  danger: 'bg-danger-soft text-danger border-danger',
};

export interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}

/** Small text label (e.g. a stage or category). Text carries the meaning; colour is decoration. */
export function Badge({ tone = 'neutral', children, className }: BadgeProps) {
  return (
    <span
      className={cn(
        // Wraps instead of overflowing on narrow screens / large text (R-5.7); the icon never shrinks.
        'inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-sm font-bold [&>svg]:shrink-0',
        toneClass[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export interface StatusPillProps {
  tone: Tone;
  children: ReactNode;
  /** Override the default icon for the tone (pass a lucide icon component). */
  icon?: LucideIcon;
  className?: string;
}

/**
 * Status with icon + text (never colour alone, R-5.7), e.g.
 * `<StatusPill tone="warning">Needs evidence</StatusPill>`.
 */
export function StatusPill({ tone, children, icon, className }: StatusPillProps) {
  const Icon = icon ?? TONE_ICONS[tone];
  return (
    <Badge tone={tone} className={className}>
      <Icon aria-hidden="true" className="h-4 w-4" />
      {children}
    </Badge>
  );
}
